<template>
  <ApexCharts
    width="100%"
    type="line"
    :options="chartOptions"
    :series="relevantHistoryData.series"
  />
</template>

<script>
import dayjs from "dayjs";
import { mapGetters } from "vuex";

export default {
  name: "BalanceChart",

  data: () => ({
    chartOptions: {
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
    },
  }),

  props: {
    fundRun: String,
  },

  watch: {
    fundRun() {
      this.updateChartOptions();
    },
    mobileBreakpoint() {
      this.updateChartOptions();
    },
  },

  computed: {
    ...mapGetters(["balanceHistory", "closeDate"]),

    relevantHistoryData() {
      // Check if the balance history data exists to prevents crashes on loading
      if (!this.balanceHistory?.balanceHistoryDict) return { series: [] };

      // Initialize data structures to store different types of balance history points
      const totalHistoryPoints = {
        CURRENT: [],
        DEPOSITS_WITHDRAWALS: [],
      };
      const balanceTypes = Object.keys(totalHistoryPoints);

      // Create a copy of the balance history data to avoid modification of the original getter
      const drawableBalanceHistory = { ...this.balanceHistory.balanceHistoryDict };

      // Gets the last date of the balance history dict
      const lastBalanceHistoryDate = Object.keys(drawableBalanceHistory)
        .sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))
        .slice(-1)[0];

      const maximumPloteableDate = this.$dayjs(this.closeDate);

      // Check if the close date is before the last balance history date
      if (maximumPloteableDate.isBefore(this.$dayjs(lastBalanceHistoryDate))) {
        // Remove the last item to plot the same last date
        delete drawableBalanceHistory[lastBalanceHistoryDate];
      }

      Object.entries(drawableBalanceHistory).forEach(historyItem => {
        const historyDate = historyItem[0];
        let fundsHistoryOnDate;

        if (!this.fundRun) fundsHistoryOnDate = { ...historyItem[1] };
        else {
          if (!historyItem[1][this.fundRun]) return;
          fundsHistoryOnDate = { [this.fundRun]: historyItem[1][this.fundRun] };
        }

        balanceTypes.map(type => totalHistoryPoints[type].push({
          date: historyDate,
          cashAmount: parseInt(
            Object.values(fundsHistoryOnDate)
              .map(fund => fund[type])
              .reduce((accumulator, cashAmount) => accumulator + cashAmount),
            10,
          ),
        }));
      });

      Object.values(totalHistoryPoints).map(balanceType => balanceType.sort((a, b) => (a.date > b.date ? 1 : b.date > a.date ? -1 : 0)));

      const totalPoints = totalHistoryPoints.CURRENT.length;
      const shrinkFactor = totalPoints < 25 ? 1 : Math.floor(totalPoints / 25);

      balanceTypes.map(
        type => (totalHistoryPoints[type] = totalHistoryPoints[type].filter(
          (balanceItem, index) => index % shrinkFactor === 0 || index === totalHistoryPoints[type].length - 1,
        )),
      );

      const series = balanceTypes.map(type => ({
        name: this.$i18n.t(
          `home.balance.balancesSections.historyChart.balanceNames.${type}`,
        ),
        data: totalHistoryPoints[type].map(point => point.cashAmount),
      }));

      return {
        dates: totalHistoryPoints.CURRENT.map(balances => balances.date),
        series,
      };
    },

    mobileBreakpoint() {
      return this.$vuetify.breakpoint.xs;
    },
  },

  methods: {
    updateChartOptions() {
      let chartColors = Array(2).fill("#B955F6");
      if (this.fundRun) {
        chartColors = Array(2).fill(this.$options.filters.addFundColor(this.fundRun));
      }
      const strokeWidth = this.mobileBreakpoint ? 2 : 3;
      const legendMarkerSizes = { width: [40, 40], height: [3, 0] };
      const dashedLine = () => "<span class='apex-dashed-line'></span>";
      const currencyFormat = value => `$${this.$options.filters.currencyNoSymbol(value)}`;
      const markersCustomHTML = [null, dashedLine];
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          chart: {
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
            },
          },
          colors: chartColors,
          xaxis: {
            categories: this.relevantHistoryData.dates,
            tickAmount: this.mobileBreakpoint ? 3 : 5,
            labels: {
              rotate: 0,
              formatter(value) {
                return dayjs(value).format("DD/MM/YY");
              },
            },
          },

          yaxis: {
            labels: {
              show: true,
              align: "right",
              minWidth: 0,
              maxWidth: 160,
              style: {
                colors: [],
                fontFamily: "Work Sans",
                fontWeight: 500,
                fontSize: "10px",
                lineHeight: "170%",
                cssClass: "apexcharts-yaxis-label",
              },
              offsetX: 0,
              offsetY: 0,
              rotate: 0,
              formatter(val) {
                if (val !== undefined) {
                  const formatCash = n => {
                    if (n < 1e3) return n;
                    if (n >= 1e3 && n < 1e6) return `${+(n / 1e3).toFixed(1)}K`;
                    if (n >= 1e6 && n < 1e9) return `${+(n / 1e6).toFixed(1)}M`;
                    if (n >= 1e9 && n < 1e12) return `${+(n / 1e9).toFixed(1)}MM`;
                    return n;
                  };
                  return `$${formatCash(val)}`;
                }

                return val;
              },
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          onItemHover: {
            highlightDataSeries: false,
          },
          onItemClick: {
            toggleDataSeries: false,
          },
          markers: {
            width: legendMarkerSizes.width,
            height: legendMarkerSizes.height,
            radius: 12,
            customHTML: markersCustomHTML,
            offsetX: 0,
            offsetY: 0,
          },
        },
        tooltip: {
          y: {
            formatter: val => currencyFormat(val),
          },
          marker: {
            show: false,
          },
        },
        grid: {
          show: true,
          strokeDashArray: 0,
          position: "back",
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
          borderColor: "#E2E0E0",
        },

        stroke: {
          width: [strokeWidth, strokeWidth],
          curve: "straight",
          dashArray: [0, strokeWidth],
        },
      };
    },
  },

  mounted() {
    this.updateChartOptions();
  },
};
</script>

<style>
.apex-dashed-line {
  width: 40px;
  border-bottom: 3px dashed;
  display: inline-block;
}
</style>
